/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  FlatList,
  RefreshControl,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useRoute, useNavigation, type RouteProp } from '@react-navigation/native';
import type { ParamListBase } from '@react-navigation/native';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { supabase } from '../../shared/supabase/supabaseClient';
import { captureCatch } from '../../shared/sentry/sentryUser';
import {
  getOrCreateOrderConversation,
  listOrderMessages,
  markOrderConversationReadByStore,
  notifyOrderMessage,
  sendOrderMessageAsStore,
  type OrderMessage,
} from '../infra/orderMessagesRepo';

export function OrderMessageThreadScreen() {
  const route = useRoute<RouteProp<ParamListBase, 'OrderMessageThread'>>();
  const navigation = useNavigation<any>();
  const params = route.params as { orderId: string; conversationId?: string };
  const [conversationId, setConversationId] = useState<string | null>(params.conversationId ?? null);
  const [messages, setMessages] = useState<OrderMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<FlatList>(null);
  // BUG #9 fix: ref to avoid double load when conversationId is created inline.
  const creatingRef = useRef(false);

  const load = useCallback(async (mode: 'initial' | 'refresh' = 'initial') => {
    if (mode === 'initial') setInitialLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      let cid = conversationId;
      if (!cid && !creatingRef.current) {
        creatingRef.current = true;
        const conv = await getOrCreateOrderConversation(params.orderId);
        cid = conv.id;
        setConversationId(cid);
      } else if (!cid) {
        // Still creating — skip this load, the state change will retrigger
        return;
      }
      const msgs = await listOrderMessages(cid);
      setMessages(msgs);
      // BUG #7 fix: log errors instead of silently swallowing
      markOrderConversationReadByStore(cid).catch(captureCatch('orders', 'mark-conversation-read-initial'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar');
    } finally {
      creatingRef.current = false;
      if (mode === 'initial') setInitialLoading(false);
      else setRefreshing(false);
    }
  }, [conversationId, params.orderId]);

  useEffect(() => {
    void load('initial');
  }, [load]);

  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase
      .channel(`store-order-messages-${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'order_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const msg = payload.new as OrderMessage;
          setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
          // BUG #7 fix: log errors instead of silently swallowing
          markOrderConversationReadByStore(conversationId).catch(captureCatch('orders', 'mark-conversation-read-realtime'));
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [conversationId]);

  async function handleSend() {
    if (!conversationId || !draft.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      const msg = await sendOrderMessageAsStore(conversationId, draft.trim());
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      setDraft('');
      void notifyOrderMessage(conversationId, msg.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  }

  return (
    <ShellContentFrame style={styles.container}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={styles.header}>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel="Volver"
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.back}>← Volver</Text>
          </TouchableOpacity>
          <Text style={styles.title}>Mensajes · #{params.orderId.slice(0, 8)}</Text>
        </View>
        {initialLoading && messages.length === 0 ? (
          <ActivityIndicator color={ticketColors.ink2} style={{ marginTop: 24 }} />
        ) : (
          <FlatList
            ref={listRef}
            data={messages}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.list}
            onContentSizeChange={() => listRef.current?.scrollToEnd({ animated: true })}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={() => void load('refresh')}
                colors={[ticketColors.gold]}
                tintColor={ticketColors.gold}
              />
            }
            ListEmptyComponent={
              <Text style={styles.empty}>Todavía no hay mensajes en este pedido.</Text>
            }
            renderItem={({ item }) => (
              <View
                style={[
                  styles.bubble,
                  item.author === 'store' ? styles.bubbleStore : styles.bubbleCustomer,
                ]}
              >
                <Text
                  style={[
                    styles.bubbleText,
                    item.author === 'store' ? styles.bubbleTextStore : null,
                  ]}
                >
                  {item.body}
                </Text>
                <Text style={styles.bubbleTime}>
                  {new Date(item.created_at).toLocaleTimeString('es-AR', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })}
                </Text>
              </View>
            )}
          />
        )}
        {error ? <Text style={styles.error}>{error}</Text> : null}
        <View style={styles.composer}>
          <TextInput
            style={styles.input}
            value={draft}
            onChangeText={setDraft}
            placeholder="Responder al cliente…"
            placeholderTextColor={ticketColors.ink2}
            multiline
          />
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel="Enviar mensaje"
            style={[styles.send, (!draft.trim() || sending) && styles.sendDisabled]}
            onPress={() => void handleSend()}
            disabled={!draft.trim() || sending}
          >
            <Text style={styles.sendText}>{sending ? '…' : 'Enviar'}</Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { paddingHorizontal: 16, paddingVertical: 12, gap: 4 },
  back: { color: ticketColors.gold, fontFamily: ticketFonts.sans, fontSize: 14 },
  title: { fontSize: 18, fontWeight: '800', color: ticketColors.ink, fontFamily: ticketFonts.sans },
  list: { padding: 16, gap: 8, paddingBottom: 24 },
  empty: { color: ticketColors.ink2, fontFamily: ticketFonts.sans, textAlign: 'center', marginTop: 24 },
  bubble: { maxWidth: '85%', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8, marginBottom: 8 },
  bubbleStore: { alignSelf: 'flex-end', backgroundColor: ticketColors.ink },
  bubbleCustomer: { alignSelf: 'flex-start', backgroundColor: ticketColors.paper2 },
  bubbleText: { color: ticketColors.ink, fontFamily: ticketFonts.sans, fontSize: 14 },
  bubbleTextStore: { color: ticketColors.paper },
  bubbleTime: { marginTop: 4, fontSize: 10, color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  composer: {
    flexDirection: 'row',
    gap: 8,
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: ticketColors.rule,
    alignItems: 'flex-end',
  },
  input: {
    flex: 1,
    minHeight: 40,
    maxHeight: 100,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  send: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  sendDisabled: { opacity: 0.5 },
  sendText: { color: ticketColors.goldInk, fontWeight: '700', fontFamily: ticketFonts.sans },
  error: {
    color: '#B91C1C',
    paddingHorizontal: 16,
    fontSize: 12,
    fontFamily: ticketFonts.sans,
  },
});
